import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import { useGetDropdownUsersQuery } from "../api/getEmployeeList";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useNavigate } from "react-router-dom";
import { genericHeaders, serviceUrl } from "../utils/apiConfig";
import axios from "axios";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { toast } from 'react-toastify';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectType,
  setEmployeeId,
  setDate,
  setSelectMonth,
  setPayDetails,
  setError,
  setPayload,
  setAdditionalFields,
  setEmployeeType,
  setResponseData,
  setOptions,
  addField,
  updateField,
  setPayrollId,
  setDisablePayroll,
  setLopDays,
  setGrossPayWithoutLop,
} from "../store/reducers/payrollSlice";
import PayrollLoader from "../Components/customComponents/customLoaderPayroll";
import { DeleteOutline } from "@mui/icons-material";
import { getToken } from "../services/Token";
import { useLazyGetLockedMonthsQuery } from "../api/payRollApi";

function Payroll() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const selectType = useSelector((state) => state.payroll.selectType);
  const selectMonth = useSelector((state) => state.payroll.selectMonth);
  const lopDays = useSelector((state) => state.payroll.lopDays);
  const grossPayWithoutLop = useSelector((state) => state.payroll.grossPayWithoutLop);
  const additionalFields = useSelector(
    (state) => state.payroll.additionalFields
  );
  const payDetails = useSelector((state) => state.payroll.payDetails);
  const employeeType = useSelector((state) => state.payroll.employeeType);
  const error = useSelector((state) => state.payroll.error);
  const payload = useSelector((state) => state.payroll.payload);
  const getPayload = useSelector((state) => state.payroll.getPayload);
  const navigation = useSelector((state) => state.payroll.navigation);
  const payrollId = useSelector((state) => state.payroll.payrollId);
  const disablePayroll = useSelector((state) => state.payroll.disablePayroll);
  const [loading, setLoading] = useState(false);
  const [payrollLoading, setPayrollLoading] = useState(false);

  const [employeeLoader, setEmployeeLoader] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const loggedInUserId = getToken("Id");
  const tenantId = getToken("userId");
  const [payroll, setPayroll] = useState({});
  const [pickerKey, setPickerKey] = useState(0);
  const [pickerView, setPickerView] = useState("year");
  const [fetchLockedMonths] = useLazyGetLockedMonthsQuery();

  const [lockedMonthsByYear, setLockedMonthsByYear] = useState({});
  const lockedMonthsByYearRef = useRef({});
  const [displayedYear, setDisplayedYear] = useState(() => {
    const preserved = selectMonth ? dayjs(selectMonth) : null;
    return preserved && preserved.isValid()
      ? preserved.year()
      : dayjs().year();
  });

  const loadLockedMonths = useCallback(
    async (year, force = false) => {
      if (!year) return null;
      const cached = lockedMonthsByYearRef.current[year];
      if (!force && cached !== undefined) return cached;
      try {
        const data = await fetchLockedMonths({ tenantId, year }).unwrap();
        const months = Array.isArray(data?.lockedMonths)
          ? data.lockedMonths.map(Number).filter((m) => m >= 1 && m <= 12)
          : [];
        lockedMonthsByYearRef.current = {
          ...lockedMonthsByYearRef.current,
          [year]: months,
        };
        setLockedMonthsByYear(lockedMonthsByYearRef.current);
        return months;
      } catch (err) {
        console.error("fetchlockedmonths failed for", year, err);
        return null;
      }
    },
    [fetchLockedMonths, tenantId]
  );

  useEffect(() => {
    loadLockedMonths(displayedYear);
  }, [displayedYear, loadLockedMonths]);

  useEffect(() => {
    const preserved = selectMonth ? dayjs(selectMonth) : null;
    if (preserved && preserved.isValid()) {
      loadLockedMonths(preserved.year());
    }
  }, [selectMonth, loadLockedMonths]);

  const isMonthLocked = useCallback(
    (date) => {
      if (!date) return false;
      const d = dayjs(date);
      if (!d.isValid()) return false;
      const months = lockedMonthsByYear[d.year()];
      if (!Array.isArray(months)) return true; // not loaded / failed => locked
      return months.includes(d.month() + 1);
    },
    [lockedMonthsByYear]
  );

  const handleYearChange = (date) => {
    if (!date) return;
    setDisplayedYear(date.year());
    loadLockedMonths(date.year(), true);
  };

  const handlePickerOpen = () => {
    setPickerView("year");
    const shown = selectMonth ? dayjs(selectMonth) : dayjs();
    if (shown.isValid()) {
      setDisplayedYear(shown.year());
      loadLockedMonths(shown.year(), true);
    }
  };

  const resetPicker = () => {
    dispatch(setSelectMonth(""));
    setPickerView("year");
    setPickerKey((prev) => prev + 1);
  };

  const shouldDisableMonth = isMonthLocked;

  const handleSelectChange = async (picked) => {
    const date =
      picked && dayjs(picked).isValid()
        ? dayjs(picked).date(15).startOf("day")
        : picked;
    const target = dayjs(date);
    const months = target.isValid()
      ? await loadLockedMonths(target.year())
      : null;
    if (months === null) {
      toast.error(
        "Could not verify locked payroll months. Please try again."
      );
      resetPicker();
      return;
    }
    if (months.includes(target.month() + 1)) {
      toast.info(
        `Payroll is already locked for ${target.format("MMMM YYYY")}`
      );
      resetPicker();
      return;
    }

    dispatch(setDisablePayroll(false));
    dispatch(setSelectMonth(date));

    const employee = selectType;
    if (date && selectType) {
      dispatch(setError(""));
    }
    if (employee && date) {
      const response = await fetchPayrollData(employee.employeeId, date);
      if(response){
        await getEmployeeSalary(employee.employeeId);
      }
    }
  };

  const getEmployeeSalary = async (employeeId) => {
    try {
      const url = `${serviceUrl}/api/lookup/payroll/getemployeesalary?employeeId=${employeeId}`;
      let response = await axios.get(url, { headers: genericHeaders() });
      const updatedItems = payDetails?.map((item) => ({
        ...item,
        totalGrossPay: response.data,
        originalGrossPayAmount: response.data,
      }));
      dispatch(setPayDetails(updatedItems));
      if (response.data > 0) {
        dispatch(setGrossPayWithoutLop(Number(response.data) || 0));
        await calculatePayroll(response.data);
      }
    } catch (error) {
      console.log(error, "error5678");
    }
  };

  const calculatePayroll = async (grossPay) => {
    setPayrollLoading(true);
    try {
      const url = `${serviceUrl}/api/lookup/payroll/getAll`;
      let response = await axios.get(url, { headers: genericHeaders() });
      if (response.data) {
        const payrollConfig = response.data;

        const fallbackGrossPay =
          Number(payDetails?.find((item) => item.id === 2)?.totalGrossPay) ||
          0;

        // `grossPay` is the already-adjusted gross pay (after LOP, etc).
        const baseSalary =
          typeof grossPay === "number" && !Number.isNaN(grossPay)
            ? grossPay
            : fallbackGrossPay;

        const calculatedEarnings = calculateEarnings(
          payrollConfig.earnings,
          baseSalary
        );

        const calculatedDeductions = calculateDeductions(
          payrollConfig.deductions,
          baseSalary
        );

        const totalDeductions = calculatedDeductions.reduce(
          (sum, item) => sum + item.calculatedAmount,
          0
        );

        const earningsWithoutSA = calculatedEarnings
          .filter((e) => !e?.isCalculateSA)
          .reduce((acc, e) => acc + e.calculatedAmount, 0);

        const grossAmount = baseSalary ?? 0;

        const specialAllowance = baseSalary - (calculatedEarnings.find((item) => item.name === "BASIC SALARY")?.calculatedAmount || 0) - (calculatedEarnings.find((item) => item.name === "HRA (House Rent Allowance)")?.calculatedAmount || 0) - (calculatedEarnings.find((item) => item.name === "EMPLOYEER PF")?.calculatedAmount || 0);
        const updatedCalculatedEarnings = calculatedEarnings.map((item) => {
          if (item.name === "SPECIAL ALLOWANCE") {
            item.calculatedAmount = Math.round(specialAllowance);
          }
          return item;
        });

        const totalEarnings = updatedCalculatedEarnings.reduce((sum, item) => {
          let calculatedAmount = item.calculatedAmount;

          if (item.isCalculateSA && grossAmount > 0) {
            calculatedAmount = Math.round(
              grossAmount - (totalDeductions + earningsWithoutSA)
            );
          }
          return sum + calculatedAmount;
        }, 0);

        const netPay = Math.round(baseSalary - totalDeductions);

        const grossPayFields = updatedCalculatedEarnings.map((item) => {
          if (item.isCalculateSA) {
            grossAmount > 0 &&
              (item.calculatedAmount = Math.round(
                grossAmount - (totalDeductions + earningsWithoutSA)
              ));
          }
          return {
            displayText: item.name,
            value: item.calculatedAmount,
          };
        });
//Special Allowance = Gross - (Basic + HRA + Employeer PF)
          //       const updatedCalculatedEarnings = calculatedEarnings.map((item) => {
          //   if(item.name === "SPECIAL ALLOWANCE"){
          //     item.calculatedAmount = specialAllowance;
          //   }
          //   return item;
          // });
        
        // const updatedGrossPayFields = grossPayFields.map((item) => {
        //   if (item.displayText === "SPECIAL ALLOWANCE") {
        //     item.value = specialAllowance.toString();
        //   }
        //   return item;
        // });
        const deductionFields = calculatedDeductions.map((item) => {
          if(item.name === "LOP (Loss of Pay)"){
            item.calculatedAmount = lopRefValue.current;
          }
          return {
            displayText: item.name,
            value: item.calculatedAmount,
          }
        });

        dispatch(
          setAdditionalFields({ key: "GROSS PAY", value: grossPayFields })
        );
        dispatch(
          setAdditionalFields({ key: "DEDUCTION", value: deductionFields })
        );

        const updatedPayDetails = payDetails.map((item) => {
          let newItem = { ...item };
          switch (newItem.id) {
            case 1:
              newItem["totalNetPay"] = Math.round(netPay);
              break;
            case 2:
              newItem["totalGrossPay"] =
                typeof grossPay === "number" && !Number.isNaN(grossPay)
                  ? Math.round(Number(grossPay))
                  : item?.totalGrossPay;
              break;
            case 3:
              newItem["totalDeductionPay"] = parseFloat(totalDeductions.toFixed(2));
              break;
            default:
              break;
          }
          return newItem;
        });
        dispatch(setPayDetails(updatedPayDetails));

        setSnackBarMessage(
          "Payroll configuration loaded and calculated successfully!"
        );
        setSnackOpen(true);
      }

      setPayroll(response.data);
      setPayrollLoading(false);
    } catch (error) {
      console.log(error, "error creating payroll");
      setPayrollLoading(false);
      setSnackBarMessage("Error loading payroll configuration");
      setSnackOpen(true);
    }
  };

  const calculateEarnings = (earnings, baseSalary) => {
    return earnings
      .filter((item) => item.isActive)
      .map((item) => {
        let calculatedAmount = 0;

        if (item.isCalcuationNeeded) {
          calculatedAmount = (baseSalary * item.perecentageofShare) / 100;

          if (item.maxCappedAmount > 0) {
            calculatedAmount = Math.min(calculatedAmount, item.maxCappedAmount);
          }
        } else {
          calculatedAmount = item.maxCappedAmount;
        }

        return {
          ...item,
          calculatedAmount: Math.round(calculatedAmount),
        };
      });
  };

  const calculateDeductions = (deductions, baseSalary) => {
    return deductions
      .filter((item) => item.isActive)
      .map((item) => {
        let calculatedAmount = 0;

        if (item.isCalcuationNeeded) {
          calculatedAmount = (baseSalary * item.perecentageofShare) / 100;

          if (item.maxCappedAmount > 0) {
            calculatedAmount = Math.min(calculatedAmount, item.maxCappedAmount);
          }
        } else {
          calculatedAmount = item.maxCappedAmount;
        }

        return {
          ...item,
          calculatedAmount: Math.round(calculatedAmount),
        };
      });
  };

  const fetchPayrollData = async (employeeId, dateTime) => {
    dispatch(setDate(dateTime));
    try {
      setLoading(true);

      let response = await axios.post(
        `${serviceUrl}/api/payrollmanagement/fetchpayrollbyempidanddate`,
        { ...getPayload, employeeId, dateTime: dateTime },
        { headers: genericHeaders() }
      );
      const length = response?.data?.totalGrossPayDetails?.length || 0;

      const date = new Date(dateTime);
      date.setMonth(date.getMonth() - 1);

      if(length === 0){
        response = await axios.post(
        `${serviceUrl}/api/payrollmanagement/fetchpayrollbyempidanddate`,
        { ...getPayload, employeeId, dateTime: date.toISOString() },
        { headers: genericHeaders() }
      );
      response.data._id = null;
      }
      dispatch(setGrossPayWithoutLop(response?.data?.originalGrossPayAmount || 0));
      dispatch(setLopDays(response?.data?.numberOfLossOfPayDays || 0));
      dispatch(setPayrollId(response?.data?._id));
      if (response.status === 200) {
        setLoading(false);
      }

      let payDetailsModifed = payDetails.map((item) => {
        let newItem = { ...item };
        switch (newItem.id) {
          case 1:
            newItem["totalNetPay"] = response?.data?.totalNetPay;
            break;
          case 2:
            newItem["totalGrossPay"] = response?.data?.totalGrossPay;
            break;
          case 3:
            newItem["totalDeductionPay"] = response?.data?.totalDeduction;
            break;
          default:
            break;
        }
        return newItem;
      });

      if (response?.data?.totalDeductionDetails) {
        let totalDeductionPayDetals = response?.data?.totalDeductionDetails;

        let deductionFields = totalDeductionPayDetals.map((item) => ({
          displayText: item.displayText,
          value: item.value,
        }));

        dispatch(
          setAdditionalFields({ key: "DEDUCTION", value: deductionFields })
        );
      }

      if (response?.data?.totalGrossPayDetails?.length > 0) {
        let totalGrossPayDetals = response?.data?.totalGrossPayDetails;
        let grossPayFields = totalGrossPayDetals.map((item) => ({
          displayText: item.displayText,
          value: item.value,
        }));
        dispatch(
          setAdditionalFields({ key: "GROSS PAY", value: grossPayFields })
        );
      }

      dispatch(setResponseData(response.data));
      dispatch(setPayDetails(payDetailsModifed));
      if(!response?.data?._id){
        return true;
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const { data: employeeData, isSuccess: employeesIsSuccess } =
    useGetDropdownUsersQuery();

  const handleChange = (event) => {
    dispatch(setEmployeeType(event.target.value));
  };

  useEffect(() => {
    if (employeesIsSuccess) {
      setEmployeeLoader(true);
    } else {
      setEmployeeLoader(false);
    }
  }, [employeesIsSuccess]);

  const handleEmployeeList = async (event) => {
    dispatch(
      setAdditionalFields({
        key: "GROSS PAY",
        value: [
          {
            displayText: "Basic Salary",
            value: "",
          },
          {
            displayText: "House Rent Allowence",
            value: "",
          },
          {
            displayText: "Conveyance",
            value: "",
          },
          {
            displayText: "Medical Allowance",
            value: "",
          },
          {
            displayText: "PF Contribution",
            value: "",
          },
          {
            displayText: "Gratituty",
            value: "",
          },
          {
            displayText: "Special Allowance",
            value: "",
          },
          // {
          //   displayText: "Gross Salary",
          //   value: "",
          // },
        ],
      })
    );

    dispatch(setAdditionalFields({ key: "DEDUCTION", value: [] }));

    const updatedItems = payDetails?.map((item) => ({
      ...item,
      totalNetPay: "",
    }));
    dispatch(setPayDetails(updatedItems));
    const employeeListId = event.target.value;
    const employee = employeeData.find((emp) => emp.employeeId === employeeListId);
    dispatch(setSelectType(employee));
    const dateTime = selectMonth !== "" ? dayjs(selectMonth) : null;
    if (employee && dateTime) {
      dispatch(setError(""));
      const response = await fetchPayrollData(employee.employeeId, dateTime);
      if (response) {
        await getEmployeeSalary(employee.employeeId);
      }
    }
  };

  useEffect(() => {
    dispatch(
      setPayload({
        totalGrossPayDetails: additionalFields["GROSS PAY"] || [],
        totalDeductionDetails: additionalFields["DEDUCTION"] || [],
      })
    );
  }, [additionalFields]);

  useEffect(() => {
    let totalNetPayPayload = 0;
    let totalGrossPayPayload = 0;
    let totalDeductionPayload = 0;
    payDetails.forEach((item) => {
      if (item.id === 1) {
        totalNetPayPayload = item.totalNetPay;
      }
      if (item.id === 2) {
        totalGrossPayPayload = item.totalGrossPay;
      }
      if (item.id === 3) {
        totalDeductionPayload = item.totalDeductionPay;
      }
    });
    dispatch(
      setPayload({
        ...payload,
        totalNetPay: totalNetPayPayload,
        totalGrossPay: totalGrossPayPayload,
        totalDeduction: totalDeductionPayload,
      })
    );
  }, [payDetails]);
  const handleAddField = (item) => {
    dispatch(addField({ item }));
  };

  useEffect(() => {
    if (!navigation) {
      dispatch(setSelectMonth(""));
      dispatch(setSelectType(""));
    }
  }, []);

  const handlePayChange = (e, id) => {
    let payAmount = 0;
    if (/^\d+$/.test(e.target.value) || e.target.value === "") {
      payAmount = e.target.value;
    }
    const payDetailsChange = payDetails.map((item) => {
      let newPayDetails = { ...item };
      switch (id) {
        case 1:
          newPayDetails["totalNetPay"] = payAmount;
          break;
        case 2:
          newPayDetails["totalGrossPay"] = payAmount;
          break;
        case 3:
          newPayDetails["totalDeductionPay"] = payAmount;
          break;
        default:
          break;
      }
      return newPayDetails;
    });
    dispatch(setPayDetails(payDetailsChange));

    if (id === 2) {
      dispatch(setGrossPayWithoutLop(Number(payAmount) || 0));
    }
  };
  useEffect(() => {}, [additionalFields]);

  const lopRefValue = useRef(0);
  const calculateLop = async (fieldName, index, value, type) => {
  if (!/^\d+$/.test(value) && value !== "") return;
  const grossPay = Number(grossPayWithoutLop) || 0;
  const lopDaysNumber = value === "" ? 0 : parseFloat(value) || 0;
  dispatch(setLopDays(lopDaysNumber));

  let lopValueRounded = 0;
  if (grossPay > 0 && value !== "" && selectMonth) {
    // Parse the selectMonth date string "2021-02-24T18:30:00.000Z"
    const date = new Date(selectMonth);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth(); // 0-indexed

    // Get total days in that month
    const totalDaysInMonth = new Date(year, month + 1, 0).getDate();

    // Per day salary
    const perDaySalary = grossPay / totalDaysInMonth;

    // LOP amount to deduct
    lopValueRounded = Math.round(perDaySalary * lopDaysNumber);
  }

  const lopValue = `${lopValueRounded}`;
  // Subtract LOP from gross pay and recalculate payroll based on adjusted gross.
  const adjustedGrossPay = Math.max(0, grossPay - lopValueRounded);
  // Update your state here — adjust to your state structure
  // const updatedPayDetails = payDetails.map((item, i) => {
  //   if (i === index) {
  //     return { ...item, [fieldName]: value, lopValue: lopValue };
  //   }
  //   return item;
  // });
  handleInputChange(fieldName, index, lopValue, type);
  lopRefValue.current = lopValue;
  // setPayDetails(updatedPayDetails);
  await calculatePayroll(adjustedGrossPay);
};
  const handleInputChange = (fieldName, index, value, type) => {
    dispatch(updateField({ fieldName, index, value, type }));
    const updatedAdditionalFields = additionalFields[fieldName].map(
      (field, idx) => {
        if (idx === index) {
          if (type === "value") {
            if (/^\d+$/.test(value) || value === "") {
              return {
                ...field,
                value: value,
              };
            }
          }
          if (type === "displayText") {
            return {
              ...field,
              displayText: value,
            };
          }
        }
        return field;
      }
    );

    let totalAdditionalFieldValue = 0;
    updatedAdditionalFields.forEach((field) => {
      if (fieldName === "GROSS PAY" && field.value !== "") {
        const parsedValue = parseInt(field.value);
        if (!isNaN(parsedValue)) {
          totalAdditionalFieldValue += parsedValue;
        }
      }
      if (fieldName === "DEDUCTION" && field.value !== "") {
        if (field.displayText === "LOP (Loss of Pay)") {
          return;
        }
        const parsedValue = parseInt(field.value);
        if (!isNaN(parsedValue)) {
          totalAdditionalFieldValue += parsedValue;
        }
      }
    });

    if (fieldName === "GROSS PAY") {
      dispatch(setGrossPayWithoutLop(totalAdditionalFieldValue));
    }

    let updatedPayDetails = payDetails.map((item) => {
      let updatedItem = { ...item };
      if (fieldName === "GROSS PAY" && item.id === 2) {
        updatedItem.totalGrossPay = totalAdditionalFieldValue;
      }
      if(fieldName === "DEDUCTION" && item.id === 1){
        const grossPay = payDetails.find((item) => item.name === "GROSS PAY")?.totalGrossPay || 0;
        updatedItem.totalNetPay = grossPay - totalAdditionalFieldValue;
      }
      if (fieldName === "DEDUCTION" && item.id === 3) {
        updatedItem.totalDeductionPay = totalAdditionalFieldValue;
      }
      return updatedItem;
    });
        dispatch(
      setAdditionalFields({
        key: fieldName,
        value: updatedAdditionalFields,
        displayText: value,
      })
    );
    dispatch(setPayDetails(updatedPayDetails));
  };

  const handleProcessPayroll = async () => {
    try {
      let totalNetPay = 0;
      let totalDeduction = 0;
      let totalGrossPay = 0;
      payDetails.forEach((item) => {
        if (item.id === 1) {
          totalNetPay = item.totalNetPay;
        }
        if (item.id === 2) {
          totalGrossPay = item.totalGrossPay;
        }
        if (item.id === 3) {
          totalDeduction = item.totalDeductionPay;
        }
      });
      if (totalNetPay > totalGrossPay || totalDeduction > totalGrossPay) {
        dispatch(
          setError("Net or Deduction value cannot be greater than Gross Pay")
        );
        return;
      }
      if (!selectType || !selectMonth) {
        dispatch(setError("Please Select the employee and month"));
        return;
      } else if (
        totalGrossPay == 0 ||
        totalNetPay == 0 ||
        totalDeduction == 0
      ) {
        dispatch(setError("Please enter all the fields"));
        return;
      }
      const fieldsHaveText = Object.values(additionalFields)
        .filter((fields) => fields !== undefined)
        .every((fields) =>
          fields.every(
            (field) => field.displayText && field.displayText.trim() !== ""
          )
        );

      if (!fieldsHaveText) {
        dispatch(setError("Please ensure all fields have display text"));
        return;
      }

      if (
        totalNetPay < totalGrossPay ||
        totalDeduction < totalGrossPay ||
        selectType ||
        selectMonth
      ) {
        setLoading(true);
        dispatch(setError(""));
        let response = await axios.post(
          
          `${serviceUrl}/api/payrollmanagement/createpayroll`,
          {
            ...payload,
            employeeId: selectType.employeeId,
            payRollProcessedDate: selectMonth,
            _id: payrollId,
            numberOfLossOfPayDays: lopDays,
            originalGrossPayAmount: grossPayWithoutLop,
          },
          { headers: genericHeaders() }
        );
        if (response.status === 201) {
          setDialogOpen(false);
          dispatch(setDisablePayroll(true));
          dispatch(setPayrollId(response?.data?._id));
          setLoading(false);
          setSnackBarMessage("Payroll updated successfully!");
          setSnackOpen(true);
        }

        let payDetailsModifed = payDetails.map((item) => {
          let newItem = { ...item };
          switch (newItem.id) {
            case 1:
              newItem["totalNetPay"] = response?.data?.totalNetPay;
              break;
            case 2:
              newItem["totalGrossPay"] = response?.data?.totalGrossPay;
              break;
            case 3:
              newItem["totalDeductionPay"] = response?.data?.totalDeduction;
              break;
            default:
              break;
          }
          return newItem;
        });
        if (response?.data?.totalDeductionDetails) {
          let totalDeductionPayDetals = response?.data?.totalDeductionDetails;
          let deductionFields = totalDeductionPayDetals.map((item) => ({
            displayText: item.displayText,
            value: item.value,
          }));
          dispatch(
            setAdditionalFields({ key: "DEDUCTION", value: deductionFields })
          );
        }

        if (response?.data?.totalGrossPayDetails) {
          let totalGrossPayDetals = response?.data?.totalGrossPayDetails;
          let grossPayFields = totalGrossPayDetals.map((item) => ({
            displayText: item.displayText,
            value: item.value,
          }));
          dispatch(
            setAdditionalFields({ key: "GROSS PAY", value: grossPayFields })
          );
        }

        dispatch(setResponseData(response.data));
        dispatch(setPayDetails(payDetailsModifed));
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleProcessPayrollClick = async () => {
    let totalNetPay = 0;
    let totalDeduction = 0;
    let totalGrossPay = 0;
    payDetails.forEach((item) => {
      if (item.id === 1) {
        totalNetPay = item.totalNetPay;
      }
      if (item.id === 2) {
        totalGrossPay = item.totalGrossPay;
      }
      if (item.id === 3) {
        totalDeduction = item.totalDeductionPay;
      }
    });
    if (!selectType) {
      dispatch(setError("Please Select the employee"));
      return;
    } else if (!selectMonth) {
      dispatch(setError("Please Select the month"));
      return;
    }

    const target = dayjs(selectMonth);
    if (!target.isValid()) {
      dispatch(setError("Please Select the month"));
      return;
    }

    const lockedForTargetYear = await loadLockedMonths(target.year());
    if (lockedForTargetYear === null) {
      dispatch(
        setError("Could not verify locked payroll months. Please try again.")
      );
      return;
    }
    if (lockedForTargetYear.includes(target.month() + 1)) {
      dispatch(
        setError(
          `Payroll is locked for ${target.format(
            "MMMM YYYY"
          )}. Please select another month.`
        )
      );
      return;
    }

    if (totalGrossPay == 0 || totalNetPay == 0 || totalDeduction == 0) {
      dispatch(setError("Please enter all the fields"));
      return;
    } else if (totalNetPay > totalGrossPay || totalDeduction > totalGrossPay) {
      dispatch(
        setError("Net or Deduction value cannot be greater than Gross Pay")
      );
      return;
    }

    if (selectType && selectMonth) {
      setError("");
      dispatch(setEmployeeId(selectType.employeeId));
      navigate("/payrollProcessing");
    }
  };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const minDate = dayjs("2000-01-01");
  const maxDate = dayjs();


  const hasInvalidGrossPay =
    !disablePayroll && payDetails?.some((item) => item?.totalGrossPay <= 0);

  return (
    <>
      {!employeeLoader && (
        <PayrollLoader text="Loading Data . Please Wait..." />
      )}

      <div className="flex flex-col w-full overflow-x-auto px-4">
        <div className="flex flex-col border border-gray-300 rounded-2xl bg-white w-full mt-8 shadow-lg overflow-x-auto p-6">
          <div className="flex items-center flex-wrap 2xl:flex-nowrap gap-3">
            <div className="flex flex-row gap-3 items-center">
              <div className="flex flex-col">
                <h1 className="text-gray-800 font-bold text-lg">
                  Employee Type:
                </h1>
                <Select
                  id="employeeType"
                  name="employeeType"
                  value={employeeType}
                  onChange={handleChange}
                  className="mt-2 w-64 rounded-lg border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200"
                >
                  <MenuItem value="currentEmployee">Current Employee</MenuItem>
                  {/* <MenuItem value="resignedEmployee">Resigned Employee</MenuItem> */}
                </Select>
              </div>
              <div className="flex flex-col">
                <h1 className="text-gray-800 font-bold text-lg mb-1.5">
                  Select Month:
                </h1>
                <div className="h-[50px]">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box
                      onClick={(event) => {
                        const hasValue = !disablePayroll && additionalFields["GROSS PAY"]?.some(
                          (item) => item.value !== ""
                        );
                        if (hasValue) {
                          event.stopPropagation();
                          setDialogOpen(true);
                        }
                      }}
                    >
                      <DesktopDatePicker
                        key={pickerKey}
                        minDate={minDate}
                        maxDate={maxDate}
                        value={selectMonth ? dayjs(selectMonth) : null}
                        views={["year", "month"]}
                        openTo="year"
                        view={pickerView}
                        onViewChange={(view) => setPickerView(view)}
                        onOpen={handlePickerOpen}
                        onClose={() => setPickerView("year")}
                        onAccept={(date) => {
                          handleSelectChange(date);
                        }}
                        onYearChange={handleYearChange}
                        shouldDisableMonth={shouldDisableMonth}
                        sx={{
                          maxHeight: 50,
                          width: "15rem",
                          bgcolor: "white",
                          marginTop: "auto",
                          marginBottom: "auto",
                          "& .MuiInputBase-root": {
                            height: 50,
                          },
                          "& .MuiPickersMonth-monthButton": {
                            borderRadius: "16px",
                          },
                          "& .MuiPickersMonth-monthButton.Mui-selected": {
                            backgroundColor: "blue",
                            color: "white",
                            "&:hover": { backgroundColor: "blue" },
                            "&:focus": { backgroundColor: "blue" },
                          },
                          "& .MuiPickersMonth-monthButton.Mui-disabled": {
                            backgroundColor: "transparent",
                            color: "#00000042",
                            textDecoration: "line-through",
                          },
                          borderRadius: "0.375rem",
                        }}
                      />
                    </Box>
                  </LocalizationProvider>
                </div>
              </div>
              {employeeType === "currentEmployee" && employeesIsSuccess && (
                <div className="flex flex-col">
                  <h1 className="text-gray-800 font-bold text-lg">
                    Employee List:
                  </h1>
                  <Select
                    id="employeeList"
                    name="employeeList"
                    value={selectType ? selectType.employeeId : "Select Type"}
                    onChange={handleEmployeeList}
                    className="mt-2 w-64 rounded-lg border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200"
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          overflowY: "auto",
                        },
                      },
                    }}
                  >
                    {employeeData.map((employee) => (
                      <MenuItem key={employee.employeeId} value={employee.employeeId}>
                        {employee?.employeeId != loggedInUserId && employee?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )}
            </div>
            <div className="flex justify-end items-center gap-3 2xl:mt-[30px] mt-3 mr-2">
              <div>
                <button
                  className="text-white bg-blue-600 border border-blue-600 hover:bg-blue-700 rounded-lg py-2 px-6 font-bold"
                  onClick={() => {
                    handleProcessPayroll();
                  }}
                >
                  Update Salary
                </button>
              </div>
              <div>
                <button
                  className={`${
                    !disablePayroll
                      ? "text-black bg-gray-300 border border-gray-300 hover:bg-gray-300 "
                      : "text-white bg-blue-600 border border-blue-600 hover:bg-blue-700"
                  }  rounded-lg py-2 px-6 font-bold`}
                  onClick={() => {
                    handleProcessPayrollClick();
                  }}
                  disabled={!disablePayroll}
                >
                  Process Payroll
                </button>
              </div>
              <div>
                <button
                  title={
                    hasInvalidGrossPay
                      ? "To calculate payroll, please enter the gross pay amount."
                      : undefined
                  }
                  className={`rounded-lg py-2 px-6 font-bold ${
                    hasInvalidGrossPay
                      ? "text-black bg-gray-300 border border-gray-300 hover:bg-gray-300"
                      : "text-white bg-blue-600 border border-blue-600 hover:bg-blue-700"
                  }`}
                  onClick={() => {
                    dispatch(setLopDays(0));
                    lopRefValue.current = 0;
                    calculatePayroll()}}
                  disabled={hasInvalidGrossPay}
                >
                  Calculate Payroll
                </button>
              </div>
              {error && <p className="text-red-500 font-medium">{error}</p>}
            </div>
          </div>
        </div>

        {payrollLoading && <PayrollLoader text="" />}

        {loading && (
          <PayrollLoader text="Data is submitting . Please Wait..." />
        )}
        {selectType?.employeeId && selectMonth && (
          <div className="my-4">
            <div className="bg-white rounded-xl">
              {selectType?.employeeId && (
                <div className="flex flex-col justify-between p-4">
                  {payDetails.map((item, index) => (
                    <div key={item.id} className="flex flex-col">
                      <div className="flex flex-row justify-between border border-b-gray-300 p-4">
                        <div className="flex gap-1">
                          <h1 className="text-gray-700 font-bold">
                            {item.name}
                          </h1>
                          {item.name !== "NET PAY" && (
                            <span>
                              <button
                                type="button"
                                onClick={() => handleAddField(item.name)}
                                className="cursor-pointer"
                              >
                                {/* <AddCircleIcon /> */}
                              </button>
                            </span>
                          )}
                        </div>
                        <input
                          className="border border-gray-200 rounded-xl p-2"
                          type="text"
                          disabled={!!(item.id === 3)}
                          value={(() => {
                            switch (item.id) {
                              case 1:
                                return item.totalNetPay;
                              case 2:
                                return item.totalGrossPay;
                              case 3:
                                return item.totalDeductionPay;
                              default:
                                return null; // Or some other default value if needed
                            }
                          })()}
                          onChange={(e) => handlePayChange(e, item.id)}
                        />
                      </div>
                      {additionalFields[item.name] &&
                        additionalFields[item.name].length > 0 &&
                        additionalFields[item.name].map((field, index) => {
                          return (
                            <div
                              key={item.id}
                              className="flex flex-row justify-between border border-b-gray-300 p-4"
                            >
                              <div>
                                <input
                                  disabled
                                  className="border border-gray-200 rounded-xl p-2 w-[250px]"
                                  type="text"
                                  value={field.displayText}
                                  onChange={(e) =>
                                    handleInputChange(
                                      item.name,
                                      index,
                                      e.target.value,
                                      "displayText"
                                    )
                                  }
                                />
                              </div>
                              <div>
                                {
                                  field.displayText === "LOP (Loss of Pay)" ? (
                                    <input
                                      className="border border-gray-200 rounded-xl p-2 mr-2"
                                      type="text"
                                      max={31}
                                      min={0}
                                      value={lopDays}
                                      placeholder="No.of LOP Days"
                                      onChange={(e) =>
                                    calculateLop(
                                      item.name,
                                      index,
                                      e.target.value,
                                      "value"
                                    )
                                  }
                                    />
                                  ) : null

                                }
                                <input
                                  className="border border-gray-200 rounded-xl p-2"
                                  type="text"
                                  value={field?.value ?? ""}
                                  onChange={(e) =>
                                    handleInputChange(
                                      item.name,
                                      index,
                                      e.target.value,
                                      "value"
                                    )
                                  }
                                  // disabled= {field.displayText === "PROFESSIONAL TAX" ? false : true}
                                />
                                {/* comment out if delete is required */}
                                {/* <button
                                  onClick={() => {
                                    const newGrossPay = [
                                      ...additionalFields[item.name],
                                    ];
                                    newGrossPay.splice(index, 1);
                                    dispatch(
                                      setAdditionalFields({
                                        key: item.name,
                                        value: newGrossPay,
                                      })
                                    );
                                  }}
                                  className="text-[red] ml-2"
                                >
                                  <DeleteOutline />
                                </button> */}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={snackOpen}
          autoHideDuration={3000}
          onClose={handleSnackClose}
        >
          <Alert
            severity="success"
            style={{ backgroundColor: "green", color: "white" }}
            onClose={handleSnackClose}
          >
            {snackBarMessage}
          </Alert>
        </Snackbar>
      </div>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {/* <DialogTitle id="alert-dialog-title">Restricted Holiday</DialogTitle> */}
        <DialogContent>Are you sure ? Changes will be lost.</DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              dispatch(
                setAdditionalFields({
                  key: "GROSS PAY",
                  value: [
                    {
                      displayText: "Basic Salary",
                      value: "",
                    },
                    {
                      displayText: "House Rent Allowence",
                      value: "",
                    },
                    {
                      displayText: "Conveyance",
                      value: "",
                    },
                    {
                      displayText: "Medical Allowance",
                      value: "",
                    },
                    {
                      displayText: "PF Contribution",
                      value: "",
                    },
                    {
                      displayText: "Gratituty",
                      value: "",
                    },
                    {
                      displayText: "Special Allowance",
                      value: "",
                    },
                    // {
                    //   displayText: "Gross Salary",
                    //   value: "",
                    // },
                  ],
                })
              );
              dispatch(setAdditionalFields({ key: "DEDUCTION", value: [] }));

              const updatedItems = payDetails?.map((item) => ({
                ...item,
                totalNetPay: "",
                totalGrossPay: "",
                totalDeductionPay: "",
              }));
              dispatch(setPayDetails(updatedItems));
              setDialogOpen(false);
            }}
          >
            YES
          </Button>
          <Button
            onClick={async () => {
              await handleProcessPayroll();
            }}
          >
            Update Salary & Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Payroll;
